import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { WebSocketService, UserPublicInfo } from './websocket.service';
import { LocationService } from './location.service';
import { ConfigService } from './config.service';

export interface NearbyUser {
  userInfo: UserPublicInfo;
  distance: number;
  lastSeen: Date;
  isNewDetection?: boolean;
}

export interface ProximitySettings {
  enabled: boolean;
  radiusMeters: number;
  autoJoinChat: boolean;
  notificationsEnabled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ProximityService {
  private nearbyUsersSubject = new BehaviorSubject<NearbyUser[]>([]);
  private settingsSubject = new BehaviorSubject<ProximitySettings>({
    enabled: false,
    radiusMeters: 1000,
    autoJoinChat: false,
    notificationsEnabled: true
  });

  private currentLocation: { latitude: number, longitude: number } | null = null;
  private locationUpdateInterval: any = null;

  public nearbyUsers$ = this.nearbyUsersSubject.asObservable();
  public settings$ = this.settingsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private wsService: WebSocketService,
    private locationService: LocationService,
    private configService: ConfigService
  ) {
    this.initializeWebSocketSubscriptions();
    this.loadSettings();
  }

  private initializeWebSocketSubscriptions(): void {
    // Usuario cercano detectado
    this.wsService.messages$.subscribe(message => {
      if (message.type === 'NearbyUserDetected') {
        this.handleNearbyUserDetected(message.data);
      } else if (message.type === 'NearbyUserLeft') {
        this.handleNearbyUserLeft(message.data.user_id);
      }
    });
  }

  private handleNearbyUserDetected(data: { user_info: UserPublicInfo, distance_meters: number }): void {
    const nearbyUser: NearbyUser = {
      userInfo: data.user_info,
      distance: data.distance_meters,
      lastSeen: new Date(),
      isNewDetection: true
    };

    const currentUsers = this.nearbyUsersSubject.value;
    const existingUserIndex = currentUsers.findIndex(u => u.userInfo.id === data.user_info.id);

    if (existingUserIndex !== -1) {
      // Actualizar usuario existente
      currentUsers[existingUserIndex] = { ...nearbyUser, isNewDetection: false };
    } else {
      // Nuevo usuario
      currentUsers.push(nearbyUser);
      this.showNearbyUserNotification(nearbyUser);
    }

    this.nearbyUsersSubject.next([...currentUsers]);

    // Quitar flag de nueva detección después de un tiempo
    setTimeout(() => {
      const users = this.nearbyUsersSubject.value;
      const userIndex = users.findIndex(u => u.userInfo.id === data.user_info.id);
      if (userIndex !== -1) {
        users[userIndex] = { ...users[userIndex], isNewDetection: false };
        this.nearbyUsersSubject.next([...users]);
      }
    }, 3000);
  }

  private handleNearbyUserLeft(userId: string): void {
    const currentUsers = this.nearbyUsersSubject.value;
    this.nearbyUsersSubject.next(currentUsers.filter(u => u.userInfo.id !== userId));
  }

  private showNearbyUserNotification(user: NearbyUser): void {
    const settings = this.settingsSubject.value;
    if (!settings.notificationsEnabled) return;

    // Mostrar notificación PWA si está disponible
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('👥 Usuario cercano detectado', {
        body: `${user.userInfo.name} está a ${Math.round(user.distance)}m de distancia`,
        icon: '/assets/icons/icon-192x192.png',
        badge: '/assets/icons/icon-96x96.png',
        tag: `nearby-user-${user.userInfo.id}`,
        requireInteraction: false,
        silent: false
      });

      notification.onclick = () => {
        // Abrir chat o perfil del usuario
        window.focus();
        notification.close();
      };

      // Auto cerrar después de 5 segundos
      setTimeout(() => notification.close(), 5000);
    }
  }

  // ==================== CONFIGURACIÓN ====================

  async enableProximityNotifications(radiusMeters: number = 1000): Promise<boolean> {
    try {
      // Actualizar ubicación primero
      const position = await this.locationService.getCurrentPosition().toPromise();
      if (!position) {
        throw new Error('No se pudo obtener la ubicación');
      }

      this.currentLocation = {
        latitude: position.latitude,
        longitude: position.longitude
      };

      // Enviar ubicación al WebSocket
      const locationSuccess = this.wsService.updateLocation(
        this.currentLocation.latitude,
        this.currentLocation.longitude
      );

      if (!locationSuccess) {
        throw new Error('No se pudo actualizar la ubicación en WebSocket');
      }

      // Habilitar notificaciones de proximidad
      const proximitySuccess = this.wsService.enableProximityNotifications(radiusMeters);
      
      if (proximitySuccess) {
        const newSettings = { ...this.settingsSubject.value };
        newSettings.enabled = true;
        newSettings.radiusMeters = radiusMeters;
        this.settingsSubject.next(newSettings);
        this.saveSettings(newSettings);
        
        // Iniciar actualizaciones periódicas de ubicación
        this.startLocationUpdates();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error enabling proximity notifications:', error);
      return false;
    }
  }

  async disableProximityNotifications(): Promise<boolean> {
    const success = this.wsService.disableProximityNotifications();
    
    if (success) {
      const newSettings = { ...this.settingsSubject.value };
      newSettings.enabled = false;
      this.settingsSubject.next(newSettings);
      this.saveSettings(newSettings);
      
      // Detener actualizaciones de ubicación
      this.stopLocationUpdates();
      
      // Limpiar usuarios cercanos
      this.nearbyUsersSubject.next([]);
    }

    return success;
  }

  private startLocationUpdates(): void {
    if (this.locationUpdateInterval) {
      clearInterval(this.locationUpdateInterval);
    }

    this.locationUpdateInterval = setInterval(async () => {
      try {
        const position = await this.locationService.getCurrentPosition().toPromise();
        if (position && this.wsService.isConnected()) {
          this.currentLocation = {
            latitude: position.latitude,
            longitude: position.longitude
          };

          this.wsService.updateLocation(
            this.currentLocation.latitude,
            this.currentLocation.longitude
          );
        }
      } catch (error) {
        console.error('Error updating location:', error);
      }
    }, 30000); // Actualizar cada 30 segundos
  }

  private stopLocationUpdates(): void {
    if (this.locationUpdateInterval) {
      clearInterval(this.locationUpdateInterval);
      this.locationUpdateInterval = null;
    }
  }

  // ==================== API METHODS ====================

  async loadNearbyUsers(): Promise<NearbyUser[]> {
    if (!this.currentLocation) {
      const position = await this.locationService.getCurrentPosition().toPromise();
      if (!position) return [];
      
      this.currentLocation = {
        latitude: position.latitude,
        longitude: position.longitude
      };
    }

    try {
      const settings = this.settingsSubject.value;
      const response = await this.http.get(
        `${this.configService.getApiUrl()}/api/v1/nats/geo/nearby`,
        {
          params: {
            latitude: this.currentLocation.latitude.toString(),
            longitude: this.currentLocation.longitude.toString(),
            radius_meters: settings.radiusMeters.toString()
          }
        }
      ).toPromise() as any;

      if (response?.nearby_users) {
        return response.nearby_users.map((user: any) => ({
          userInfo: user.user_info,
          distance: user.distance_meters,
          lastSeen: new Date()
        }));
      }

      return [];
    } catch (error) {
      console.error('Error loading nearby users:', error);
      return [];
    }
  }

  // ==================== SETTINGS ====================

  updateSettings(settings: Partial<ProximitySettings>): void {
    const newSettings = { ...this.settingsSubject.value, ...settings };
    this.settingsSubject.next(newSettings);
    this.saveSettings(newSettings);
  }

  private loadSettings(): void {
    try {
      const saved = localStorage.getItem('proximity-settings');
      if (saved) {
        const settings = JSON.parse(saved);
        this.settingsSubject.next(settings);
      }
    } catch (error) {
      console.error('Error loading proximity settings:', error);
    }
  }

  private saveSettings(settings: ProximitySettings): void {
    try {
      localStorage.setItem('proximity-settings', JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving proximity settings:', error);
    }
  }

  // ==================== UTILITY METHODS ====================

  getCurrentLocation(): { latitude: number, longitude: number } | null {
    return this.currentLocation;
  }

  async requestLocationPermission(): Promise<boolean> {
    const permission = await this.locationService.requestPermission();
    return permission.granted;
  }

  async requestNotificationPermission(): Promise<boolean> {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }
}